import {
  Component,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ComponentFactoryResolver,
  ComponentRef,
} from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { ModalCadastroComponent } from '../../../../shared/modal-cadastro/modal-cadastro.component';
import { TabelasPrecoService } from '../../../../services/tabelas-preco.service';
import { getErrorMessage, scrollIntoView, setFormValue } from '../../../../general/utils';
import { lastValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { DimDatatableColumn } from '@models';
import { ItemTabelaPrecoDetalheComponent } from './item-tabela-preco-detalhe/item-tabela-preco-detalhe.component';
import swal from 'sweetalert2';
import { UnidadesEmpresaParametrosService } from '../../../../services/unidades-empresa-parametros.service';
import { ItensService } from '../../../../services/itens.service';
import { MarkupsService } from '../../../../services/markups.service';
import { MessageService } from '@services';
import { TabelasPrecoItensService } from '../../../../services/tabelas-preco-itens.service';

@Component({
  selector: 'app-tabela-preco-detalhe',
  templateUrl: './tabela-preco-detalhe.component.html',
  styleUrls: ['./tabela-preco-detalhe.component.css'],
})
export class TabelaPrecoDetalheComponent extends ModalCadastroComponent implements OnInit {
  id: number;
  precisaoPreco = 2;
  selecionados = [];
  eventosMarkupSelecionado = [];
  colunas: DimDatatableColumn[] = [
    { label: 'Id', col: 'id', invisible: true },
    { label: 'Produto', col: 'produtoNome', hide: false },
    { label: 'Unidade', col: 'unidade', hide: false },
    { label: 'Custo', col: 'custo', hide: false, currency: true },
    { label: 'Preço', col: 'preco', hide: false, currency: true },
  ];

  @ViewChild('digitacaoItensContainer', { read: ViewContainerRef, static: false })
  digitacaoItensContainer: ViewContainerRef;
  digitacaoItemComponentRef: ComponentRef<ItemTabelaPrecoDetalheComponent>;

  constructor(
    private tabelasPrecoService: TabelasPrecoService,
    private tabelasPrecoItensService: TabelasPrecoItensService,
    private resolver: ComponentFactoryResolver,
    private parametrosService: UnidadesEmpresaParametrosService,
    private markupService: MarkupsService,
    private itensService: ItensService,
    private message: MessageService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super();
  }

  cadastroForm: FormGroup;

  ngOnInit() {
    this.cadastroForm = new FormGroup({
      id: new FormControl(0),
      nome: new FormControl(null, [Validators.required]),
      markupId: new FormControl(null),
      majoracao: new FormControl('M'),
      valorAumento: new FormControl(null),
      itens: new FormArray([]),
    });

    // Add listener for majoracao changes to update validators
    this.cadastroForm.get('majoracao').valueChanges.subscribe(value => {
      this.updateValorAumentoValidators(value);
    });

    this.buscarParametrosGerais();
    this.getId();
  }

  private updateValorAumentoValidators(majoracao: string) {
    const valorAumentoControl = this.cadastroForm.get('valorAumento');

    if (majoracao === 'P') {
      valorAumentoControl.setValidators([Validators.min(0), Validators.max(100)]);
    } else {
      valorAumentoControl.setValidators([]);
    }

    valorAumentoControl.updateValueAndValidity();
  }

  get isLoading() {
    return (
      this.loading['salvando'] ||
      this.loading['excluindo'] ||
      this.loading['editando'] ||
      this.loading['dados']
    );
  }

  get itens() {
    return this.cadastroForm.get('itens').value || [];
  }

  get somatorioEventosMarkupSelecionado() {
    return this.eventosMarkupSelecionado.reduce((acc, item) => acc + item.percentual, 0);
  }

  async buscarParametrosGerais() {
    try {
      const res = await lastValueFrom(this.parametrosService.getParametrosGerais());
      if (res.casasDecimaisPreco) this.precisaoPreco = res.casasDecimaisPreco;
    } catch (error) {
      console.error(error);
    }
  }

  async getId() {
    this.id = this.route.snapshot.params['id'];
    if (!this.id) {
      return;
    }

    try {
      this.loading['dados'] = true;
      const res = await lastValueFrom(this.tabelasPrecoService.getTabelaPrecoById(this.id));
      setFormValue(this.cadastroForm, res);
      this.cadastroForm.get('majoracao').setValue('M');

      const itens = await lastValueFrom(this.itensService.getItensByTabelaPrecoId(this.id));
      itens.forEach(item => {
        (this.cadastroForm.get('itens') as FormArray).push(new FormControl(item));
      })
    } catch (error) {
      console.error(error);
    } finally {
      this.loading['dados'] = false;
    }
  }

  async submitForm() {
    try {
      this.loading['salvando'] = true;
      const formValue = { ...this.cadastroForm.value };
      delete formValue.itens;
      const res = await lastValueFrom(this.tabelasPrecoService.salvar(formValue));
      if (!this.id) {
        this.cadastroForm.get('id').setValue(res.id);
        this.id = res.id;
      }

      const obj = {
        TabelaPrecoItemId: res.id,
        Itens: this.cadastroForm.get('itens').value,
      }
      await lastValueFrom(this.tabelasPrecoItensService.salvar(obj));

      this.message.success('Tabela de preço salva com sucesso');
      this.router.navigate(['/venda/cadastros/tabelas-preco']);
    } catch (error) {
      console.error(error);
      this.message.error(getErrorMessage(error));
    } finally {
      this.loading['salvando'] = false;
    }
  }

  async excluirTabelaPreco() { }

  createViewItem(row = null) {
    const index = (this.cadastroForm.get('itens').value as any[]).indexOf(row);
    this.digitacaoItensContainer.clear();

    if (this.digitacaoItensContainer.length !== 0) {
      return;
    }

    const factory = this.resolver.resolveComponentFactory(
      ItemTabelaPrecoDetalheComponent
    );
    this.digitacaoItemComponentRef =
      this.digitacaoItensContainer.createComponent(factory);

    if (row != null) {
      const item = (this.cadastroForm.get('itens') as FormArray).controls[index];
      if (item)
        this.digitacaoItemComponentRef.instance.itemEdicao = item.value as FormControl;
    }

    this.digitacaoItemComponentRef.instance.precisaoPreco = this.precisaoPreco;
    this.digitacaoItemComponentRef.instance.somatorioMarkupSelecionado = this.somatorioEventosMarkupSelecionado;
    this.digitacaoItemComponentRef.instance.formFinished.subscribe((item) => {
      scrollIntoView('cadastro-tabela-preco');
      this.destroyView();
      if (!item) {
        return;
      }

      if (index != -1) {
        (this.cadastroForm.get('itens') as FormArray).removeAt(index);
        (this.cadastroForm.get('itens') as FormArray).insert(index, new FormControl(item));
      } else {
        (this.cadastroForm.get('itens') as FormArray).push(new FormControl(item));
      }
    });
    scrollIntoView('detalheItem');
  }

  removerItem(row) {
    const index = (this.cadastroForm.get('itens').value as any[]).indexOf(row);
    swal({
      title: 'Deseja remover item?',
      text: '',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Remover item',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.value) {
        (this.cadastroForm.get('itens') as FormArray).removeAt(index);
        this.destroyView();
      }
    });
  }

  async aplicarMajoracao() {
    const majoracao = this.cadastroForm.get('majoracao')?.value;
    const valorAumento = this.cadastroForm.get('valorAumento')?.value;
    const markupId = this.cadastroForm.get('markupId')?.value;
    if (this.cadastroForm.controls.valorAumento.invalid) {
      return;
    }

    if (!this.selecionados.length) {
      this.message.info('Necessário selecionar itens para aplicar a majoração');
      return;
    }

    if (majoracao === 'M') {
      if (!markupId) {
        this.message.info('Necessário selecionar um markup para aplicar a majoração');
        return;
      }

      try {
        this.aplicarPercentualMarkup();
      } catch (error) {
        console.error(error);
        this.message.error(getErrorMessage(error));
        return;
      }
    }

    if (majoracao === 'P') {
      if (!valorAumento) {
        this.message.info('Informe o percentual de aumento');
        return;
      }

      this.aplicarPercentual(valorAumento);
    }

    if (majoracao === 'V') {
      if (!valorAumento) {
        this.message.info('Informe o valor de aumento');
        return;
      }

      this.aplicarValorFixo(valorAumento);
    }

    this.message.success('Aumento aplicado com sucesso', 5000);
    if (majoracao === 'M' && this.selecionados.some(item => !item.custo)) {
      this.message.info('Itens sem custo informado não sofreram alterações', 6000, 'Atenção', false);
    }
    this.selecionados = [];
    this.cadastroForm.patchValue({
      majoracao: 'M',
      valorAumento: null,
    });
  }

  private aplicarPercentualMarkup() {
    const itens = this.cadastroForm.get('itens')?.value || [];

    const atualizados = itens.map((item: any) =>
      this.selecionados.includes(item) && !!item.custo
        ? { ...item, preco: item.custo / ((100 - this.somatorioEventosMarkupSelecionado) / 100) }
        : item
    );

    this.cadastroForm.get('itens')?.setValue(atualizados);
  }

  private aplicarPercentual(percentual: number) {
    const itens = this.cadastroForm.get('itens')?.value || [];

    const atualizados = itens.map((item: any) =>
      this.selecionados.includes(item)
        ? { ...item, preco: item.preco * (1 + percentual / 100) }
        : item
    );

    this.cadastroForm.get('itens')?.setValue(atualizados);
  }

  private aplicarValorFixo(valor: number) {
    const itens = this.cadastroForm.get('itens')?.value || [];

    const atualizados = itens.map((item: any) =>
      this.selecionados.includes(item)
        ? { ...item, preco: item.preco + valor }
        : item
    );

    this.cadastroForm.get('itens')?.setValue(atualizados);
  }

  async buscarEventosMarkup(markup: any) {
    try {
      this.eventosMarkupSelecionado = await lastValueFrom(this.markupService.getEventoById(markup.id));
    } catch (error) {
      console.error(error);
    }
  }

  destroyView() {
    if (this.digitacaoItemComponentRef) {
      this.digitacaoItemComponentRef.destroy();
      this.digitacaoItemComponentRef = null;
    }
  }

  voltar() {
    this.router.navigate(['/venda/cadastros/tabelas-preco']);
  }
}
